import type { SVGAttributes } from 'react';

export { radixSideOffset, TIP_ARROW_SIZE } from './TipArrow.size';

/**
 * Figma "Tip Arrow" (node 2218:13513) — the Tooltip/Popover pointer, passed
 * to Radix `Arrow asChild` (web only; the native arrow lives in
 * `FloatingBox.native`). Radix's default `<polygon>` strokes ALL its edges,
 * drawing a visible line along the arrow's base where it meets the surface.
 * The design asset instead is a rounded-tip pointer whose flat base sits
 * outside the viewBox: the base edge (and its stroke) is clipped away, so
 * only the two slanted sides carry the secondary-200 border and the
 * translucent fill reads as continuous with the surface.
 *
 * GEOMETRY — the artwork is 10 wide × 7.67 tall (stroked), so it needs a
 * 7-tall box, NOT Radix's default 5. It is not a plain triangle: 1.2px in
 * from the base the sides flare out to the arrow's widest span (9.76px), and
 * that flare is what makes the arrow read as growing out of the surface. Only
 * ~1.2px may be clipped (the base edge plus its stroke) — clip more and the
 * flare ends up buried inside the surface, leaving a narrow chevron that
 * looks sunk into the box rather than sitting on its edge.
 *
 * `translate(0 7)` is what lands it: the base's stroke ends at y=-0.13 (fully
 * clipped), the flare sits at y=0.59 — inside the surface's 1px border row,
 * so the fill covers the border and the sides continue it — and the tip
 * reaches y=6.54, inside the 7 box.
 *
 * NO negative margin, deliberately — Radix already provides the 1px tuck. Its
 * arrow wrapper is `position:absolute; bottom:0`, and an abspos offset
 * resolves against the containing block's PADDING box, so the wrapper starts
 * exactly 1px inside the surface's outer edge: on the border row. That puts
 * the border row at viewBox y=0..1 and the surface's outer edge at y=1, which
 * is what the translate below is tuned for. A `-mt-px` on top of that (as
 * this once had) tucks a SECOND px, cutting the arrow's outline 1px inside
 * the surface — which leaves two stubs of its sides poking up past the edge
 * and makes the arrow read as sunk into the box. Radix rotates the wrapper
 * per side, so pre-rotation "up" is always "toward the content".
 *
 * OPAQUE FILL, deliberately — the surface is glass (`bg-secondary-100/70`) but
 * the arrow is not. The arrow is a child of Radix's Content, so it paints over
 * the surface's border; the 1px `secondary-200` row it covers is the ONLY way
 * the box's edge stops running straight through the arrow's mouth. A
 * translucent fill can't erase a border, it just tints it (245 @ 70% over the
 * 229 border = a 240 line against the 248 surface — visible, and it reads as
 * the arrow sitting behind the edge rather than on it). Opaque costs a ~3/255
 * step against the glass, which is far below the line it removes.
 *
 * The path is the Figma export (a left-pointing pointer) rotated into the
 * down-pointing frame Radix expects; `TIP_ARROW_SIZE` renders it 1:1 so the
 * stroke stays 1px.
 */
// SVGAttributes (not SVGProps): `ref` is nominally incompatible between the
// two @types/react copies in the monorepo (see Pressable.tsx's casts).
export function TipArrow(props: SVGAttributes<SVGSVGElement>) {
  return (
    <svg {...props} viewBox="0 0 10 7">
      <path
        transform="translate(0 7) rotate(-90)"
        d="M7.62857 1.60462L7.62857 8.16712C7.62838 9.1794 6.45915 9.74334 5.66666 9.11341L1.56509 5.85365C0.958339 5.37135 0.955543 4.45108 1.55924 3.96497L5.6608 0.66224C6.45214 0.025392 7.62857 0.588768 7.62857 1.60462Z"
        className="fill-secondary-100 stroke-secondary-200"
      />
    </svg>
  );
}
